import { useCallback, useEffect, useMemo, useState } from 'react';
import { type DrawerProps, type DrawerRefs, type DrawerSnapPoint } from '../types';

const calcSnapSize = (snap: string | number, fullSize: number): number => {
    if (typeof snap === 'string') {
        return Number(snap.replace('px', ''));
    }

    return fullSize * snap;
};

type UseSnapComponentsParams = Pick<DrawerProps, 'isSnapChildren' | 'snapPoints'> &
    Pick<DrawerRefs, 'contentRef' | 'drawerRef' | 'containerRef'>;

export const useSnapComponents = ({
    contentRef,
    drawerRef,
    containerRef,
    isSnapChildren,
    snapPoints,
}: UseSnapComponentsParams) => {
    const [snapPointsArray, setSnapPointsArray] = useState<Array<DrawerSnapPoint>>([]);
    const [snapPoint, setSnapPoint] = useState<number>(0);

    const isSnapEnable = useMemo(() => isSnapChildren || snapPoints?.length, [isSnapChildren, snapPoints?.length]);

    const calcSnapPoints = useCallback(() => {
        if (!isSnapEnable) {
            return;
        }

        const drawer = drawerRef.current;
        const content = contentRef.current;
        const container = containerRef.current;

        if (!content || !drawer || !container) {
            return;
        }

        const contentOffsetTop = content.getBoundingClientRect().top - container.getBoundingClientRect().top;

        let newSnapPoints: Array<DrawerSnapPoint>;

        /**
         * Находим значения порогов и смещений для шагов по массиву порогов
         */
        if (snapPoints?.length) {
            newSnapPoints = snapPoints.reduce<Array<DrawerSnapPoint>>((acc, cur, index) => {
                const prev = acc[index - 1];
                const prevOffset = prev?.offset || 0;

                const snapSize = calcSnapSize(cur, window.innerHeight);

                const isFirstPoint = index === 0;
                const contentHeight = content.scrollHeight + contentOffsetTop;
                const isContentMoreSnap = contentHeight > snapSize;

                const offset = isFirstPoint && !isContentMoreSnap ? contentHeight : snapSize;

                const snap = prevOffset ? prevOffset + (offset - prevOffset) / 2 : offset;

                acc.push({ offset, snap });

                return acc;
            }, []);
            /**
             * Находим значения порогов и смещений для шагов по дочерним элементам контента
             */
        } else {
            const children = content.children;

            newSnapPoints = Array.from(children).reduce<Array<DrawerSnapPoint>>((acc, cur, index) => {
                if (cur instanceof HTMLElement) {
                    const prev = acc[index - 1];
                    const prevOffset = prev?.offset || contentOffsetTop;

                    const offset = cur.offsetTop + cur.offsetHeight + contentOffsetTop;
                    const snap = cur.clientHeight / 2 + prevOffset;

                    acc.push({ offset, snap });
                }

                return acc;
            }, []);
        }

        const filteredSnapPoints = newSnapPoints.filter((point) => point.snap <= drawer.clientHeight);

        if (newSnapPoints.length > 1 && filteredSnapPoints.length === 1) {
            return [];
        }

        return filteredSnapPoints;
    }, [containerRef, contentRef, drawerRef, isSnapEnable, snapPoints]);

    const calcSnap = useCallback(() => {
        const newSnapPoints = calcSnapPoints();

        if (!newSnapPoints) {
            return;
        }

        setSnapPointsArray(newSnapPoints);
    }, [calcSnapPoints]);

    useEffect(() => {
        if (isSnapEnable) {
            calcSnap();
        }
    }, [calcSnap, isSnapEnable]);

    const snapPointSize = useMemo(() => {
        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        if (!isSnapEnable || !containerRef.current || !snapPointsArray.length) {
            return 0;
        }

        /**
         * Смещение текушего шага
         */
        const snapPointSize = snapPointsArray[snapPoint]?.offset || 0;

        const defaultSnapSize = containerRef.current.clientHeight;

        if (!snapPointSize) {
            return defaultSnapSize;
        }

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        return Math.max(defaultSnapSize - snapPointSize, 0);
    }, [containerRef, isSnapEnable, snapPoint, snapPointsArray]);

    const resetSnap = useCallback(() => {
        setSnapPoint(0);
        setSnapPointsArray([]);
    }, []);

    // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
    return {
        currentSnapPoint: snapPoint,

        // eslint-disable-next-line react-hooks/refs -- Автоматический игнор в рамках DIRECT-288723
        snapPointSize,
        snapPointsArray,
        setSnapPoint,
        snapResizeCallback: calcSnap,
        resetSnap,
    };
};
